import { FC, useMemo, useCallback, useState } from 'react'
import styles from 'components/features/pfm/TransactionNarrow/TransactionNarrow.styl'
import cn from 'classnames'
import { Transaction } from 'interfaces/moneyflow'
import { TypedMoneyflowCategory } from 'interfaces/meta'
import { TooltipIcon } from 'components/features/pfm/Tooltip/TooltipIcon'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { TransactionCategory } from 'components/features/pfm/TransactionCategory/TransactionCategory'
import { OverlayMobile } from 'components/features/pfm/Overlay/OverlayMobile'
import TransactionEditForm from 'components/molecules/forms/TransactionEditForm'
import TextButton from 'components/atoms/buttons/TextButton'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { formatRelativeDate } from 'helpers/dateTimeHelpers'
import { capitalizeFirstLetter } from 'helpers/formatHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { OverlayContainer } from 'components/features/pfm/Overlay/OverlayContainer'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import SvgIcon from 'components/atoms/SvgIcon'
import ArrowSvg from 'icons/arrow.svg'
import { DATE_FORMAT_READABLE } from 'constants/datetime'
import { PENDING_TRANSACTIONS_HINT } from 'constants/hints'
import { TransactionName } from 'components/features/transactions/TransactionName/TransactionName'

type TransactionNarrowProps = {
  transaction: Transaction
  newCategory?: TypedMoneyflowCategory
  disableRecat?: boolean
  hideEdit?: boolean
  hideCategory?: boolean
  hideDate?: boolean
  hideLongDescription?: boolean
  showStatus?: boolean
}

export const TransactionNarrow: FC<TransactionNarrowProps> = (props) => {
  const {
    transaction,
    newCategory,
    disableRecat,
    hideEdit,
    hideDate,
    hideCategory,
    hideLongDescription,
    showStatus,
  } = props
  const { description, amount, category, date, status, account_source_type } = transaction

  const dispatch = useStoreDispatch()
  const descriptionChild = useMemo(() => {
    return description || (
      <div className={ styles.absentDescription } data-qa-type='absent-value'>Not presented</div>
    )
  }, [description])

  const amountChild = useMemo(() => {
    return formatTableCurrency(amount, 'penny')
  }, [amount])

  const isMobile = useStoreSelector(isMobileSelector)
  const editFormChild = useMemo(() => (
    <TransactionEditForm isMobile transaction={ transaction } type={ category.type! } />
  ), [transaction, category.type])

  const [isEditing, setIsEditing] = useState(false)

  const handleEdit = useCallback(() => {
    setIsEditing(true)
  }, [setIsEditing])

  const handleClose = useCallback(() => {
    setIsEditing(false)
  }, [setIsEditing])

  const handleCategoryClick = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('transaction-recat', { mode: 'category', transaction }))
  }, [dispatch, transaction])

  const isPending = status === 'pending'

  const updateToCategory = newCategory && newCategory !== transaction.category ? (
    <>
      <i className={ styles.arrowIcon }>
        <SvgIcon src={ ArrowSvg } />
      </i>
      <TransactionCategory category={ newCategory } disabled />
    </>
  ) : null

  const isStatusCardTransfer = account_source_type === 'railsbank' && category.id === 'transfer'

  const categoryBadge = !hideCategory && (
    <div className={ cn(styles.category, { [styles.constrained]: !!updateToCategory }) }>
      <TransactionCategory
        category={ category }
        onClick={ () => !disableRecat && handleCategoryClick() }
        dim={ isPending }
        disabled={ isPending || !!updateToCategory || !!disableRecat || isStatusCardTransfer }
        tooltipText={ isPending ? PENDING_TRANSACTIONS_HINT : undefined }
      />
      { updateToCategory }
    </div>
  )

  const footerIsHidden = hideCategory && hideEdit

  const categoryFooter = !footerIsHidden ? (
    <div className={ styles.footer }>
      { categoryBadge }
      { !hideEdit && <TextButton size='regular' onClick={ handleEdit }>Edit</TextButton> }
    </div>
  ) : null

  return (
    <>
      <div className={ styles.transactionNarrow }>
        <div className={ styles.header }>
          <div className={ styles.description }>{ descriptionChild }</div>
          { transaction.account_source_type === 'yodlee' && transaction.description && transaction.description_verbose && !hideLongDescription && (
            <div className={ styles.tooltip }>
              <TooltipIcon icon='ellipsis'>{ transaction.description_verbose }</TooltipIcon>
            </div>
          )}
          <div className={ styles.amount }>{ amountChild }</div>
        </div>
        <div className={ cn(styles.subheader, { [styles.removeLowerMargin]: footerIsHidden }) }>
          <span><TransactionName transaction={ transaction } /></span>
          { !hideDate && formatRelativeDate(date, DATE_FORMAT_READABLE) }
          { showStatus && <span>{ capitalizeFirstLetter(status) }</span> }
        </div>
        { categoryFooter }
      </div>
      { isEditing && isMobile && (
        <OverlayMobile onClose={ handleClose }>
          { editFormChild }
        </OverlayMobile>
      )}
      { isEditing && !isMobile && (
        <OverlayContainer onClose={ handleClose }>
          { editFormChild }
        </OverlayContainer>
      )}
    </>
  )
}
